import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css'; // Quill's default styling
import "./RichTextEditor.css";

// Create a safer image resize module with full resize functionality
class SafeImageResize {
  constructor(quill, options = {}) {
    this.quill = quill;
    this.options = {
      modules: ['Resize', 'DisplaySize'],
      handleStyles: {
        backgroundColor: '#4285f4',
        border: '1px solid #ffffff',
        borderRadius: '4px',
        width: '8px',
        height: '8px',
      },
      displayStyles: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        border: 'none',
        color: 'white',
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        padding: '4px 8px',
        borderRadius: '4px',
      },
      ...options
    };
    
    this.img = null;
    this.overlay = null;
    this.boxes = [];
    this.dragBox = null;
    this.dragStartX = null;
    this.dragStartY = null;
    this.preDragWidth = null;
    this.preDragHeight = null;
    this.isDragging = false;

    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleMousedown = this.handleMousedown.bind(this);
    this.handleMousemove = this.handleMousemove.bind(this);
    this.handleMouseup = this.handleMouseup.bind(this);
    
    // Add event listeners
    this.quill.root.addEventListener('click', this.handleClick, false);
    document.addEventListener('keyup', this.handleKeyup, false);
    document.addEventListener('mousemove', this.handleMousemove, false);
    document.addEventListener('mouseup', this.handleMouseup, false);
    this.quill.root.parentNode.style.position = this.quill.root.parentNode.style.position || 'relative';
  }

  handleClick(evt) {
    try {
      if (evt.target && evt.target.tagName && evt.target.tagName.toUpperCase() === 'IMG') {
        if (this.img === evt.target) {
          return;
        }
        if (this.img) {
          this.hide();
        }
        this.show(evt.target);
      } else if (this.img && !this.isDragging) {
        // Don't hide if we're in the middle of dragging
        let isClickOnHandle = false;
        if (evt.target && evt.target.classList) {
          isClickOnHandle = evt.target.classList.contains('ql-image-resize-handle');
        }
        if (!isClickOnHandle) {
          this.hide();
        }
      }
    } catch (error) {
      console.warn('Image click handled safely:', error);
    }
  }

  handleKeyup(evt) {
    try {
      if (!this.img) {
        return;
      }
      if (evt.keyCode === 46 || evt.keyCode == 8) {
        const selection = this.quill.getSelection();
        if (selection && this.img) {
          const blot = this.quill.constructor.find(this.img);
          if (blot) {
            blot.deleteAt(0);
          }
          this.hide();
        }
      }
    } catch (error) {
      console.warn('Key event handled safely:', error);
    }
  }

  handleMousedown(evt) {
    try {
      if (!evt.target || !evt.target.classList || !evt.target.classList.contains('ql-image-resize-handle')) {
        return;
      }

      evt.preventDefault();
      this.dragBox = evt.target;
      this.dragStartX = evt.clientX;
      this.dragStartY = evt.clientY;
      this.preDragWidth = this.img.width || this.img.naturalWidth;
      this.preDragHeight = this.img.height || this.img.naturalHeight;
      this.isDragging = true;
    } catch (error) {
      console.warn('Mouse down handled safely:', error);
    }
  }

  handleMousemove(evt) {
    try {
      if (!this.isDragging || !this.dragBox || !this.img) {
        return;
      }

      const deltaX = evt.clientX - this.dragStartX;
      const deltaY = evt.clientY - this.dragStartY;
      const direction = this.dragBox.dataset.direction;

      let newWidth = this.preDragWidth;
      let newHeight = this.preDragHeight;

      // Calculate new dimensions based on drag direction
      switch (direction) {
        case 'nw':
          newWidth = this.preDragWidth - deltaX;
          newHeight = this.preDragHeight - deltaY;
          break;
        case 'ne':
          newWidth = this.preDragWidth + deltaX;
          newHeight = this.preDragHeight - deltaY;
          break;
        case 'sw':
          newWidth = this.preDragWidth - deltaX;
          newHeight = this.preDragHeight + deltaY;
          break;
        case 'se':
          newWidth = this.preDragWidth + deltaX;
          newHeight = this.preDragHeight + deltaY;
          break;
      }

      // Maintain aspect ratio
      const aspectRatio = this.preDragWidth / this.preDragHeight;
      if (this.options.preserveAspectRatio !== false) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          newHeight = newWidth / aspectRatio;
        } else {
          newWidth = newHeight * aspectRatio;
        }
      }

      // Apply minimum constraints
      newWidth = Math.max(50, newWidth);
      newHeight = Math.max(50, newHeight);

      // Update image dimensions
      this.img.width = newWidth;
      this.img.height = newHeight;
      
      // Reposition overlay
      this.repositionElements();
    } catch (error) {
      console.warn('Mouse move handled safely:', error);
    }
  }

  handleMouseup(evt) {
    try {
      if (this.isDragging) {
        this.isDragging = false;
        this.dragBox = null;
      }
    } catch (error) {
      console.warn('Mouse up handled safely:', error);
    }
  }

  show(img) {
    try {
      this.img = img;
      this.showOverlay();
    } catch (error) {
      console.warn('Show image handled safely:', error);
    }
  }

  showOverlay() {
    if (this.overlay) {
      this.hideOverlay();
    }

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'absolute',
      boxSizing: 'border-box',
      border: '1px dashed #4285f4',
      pointerEvents: 'none',
    });

    // Create resize handles
    this.createResizeHandles();
    this.repositionElements();
    this.quill.root.parentNode.appendChild(this.overlay);
  }

  createResizeHandles() {
    const handles = ['nw', 'ne', 'sw', 'se'];
    this.boxes = [];

    handles.forEach(direction => {
      const box = document.createElement('div');
      box.classList.add('ql-image-resize-handle');
      box.dataset.direction = direction;
      
      Object.assign(box.style, {
        position: 'absolute',
        width: '8px',
        height: '8px',
        backgroundColor: '#4285f4',
        border: '1px solid #ffffff',
        borderRadius: '4px',
        cursor: this.getCursorForDirection(direction),
        pointerEvents: 'all',
        zIndex: '1001',
      });

      box.addEventListener('mousedown', this.handleMousedown, false);
      this.overlay.appendChild(box);
      this.boxes.push(box);
    });
  }

  getCursorForDirection(direction) {
    switch (direction) {
      case 'nw': return 'nw-resize';
      case 'ne': return 'ne-resize';
      case 'sw': return 'sw-resize';
      case 'se': return 'se-resize';
      default: return 'nwse-resize';
    }
  }

  hideOverlay() {
    if (!this.overlay) {
      return;
    }

    // Remove event listeners from handles
    this.boxes.forEach(box => {
      box.removeEventListener('mousedown', this.handleMousedown, false);
    });
    this.boxes = [];

    this.quill.root.parentNode.removeChild(this.overlay);
    this.overlay = null;
  }

  repositionElements() {
    if (!this.overlay || !this.img) {
      return;
    }

    const parent = this.quill.root.parentNode;
    const imgRect = this.img.getBoundingClientRect();
    const containerRect = parent.getBoundingClientRect();

    const left = imgRect.left - containerRect.left - 1;
    const top = imgRect.top - containerRect.top;
    const width = imgRect.width;
    const height = imgRect.height;

    Object.assign(this.overlay.style, {
      left: left + 'px',
      top: top + 'px',
      width: width + 'px',
      height: height + 'px',
    });

    // Position resize handles
    if (this.boxes.length >= 4) {
      // Northwest handle
      Object.assign(this.boxes[0].style, {
        left: '-4px',
        top: '-4px',
      });
      
      // Northeast handle
      Object.assign(this.boxes[1].style, {
        right: '-4px',
        top: '-4px',
      });
      
      // Southwest handle
      Object.assign(this.boxes[2].style, {
        left: '-4px',
        bottom: '-4px',
      });
      
      // Southeast handle
      Object.assign(this.boxes[3].style, {
        right: '-4px',
        bottom: '-4px',
      });
    }
  }

  hide() {
    this.hideOverlay();
    this.img = null;
    this.isDragging = false;
  }
}

// Register the safer image resize module
ReactQuill.Quill.register('modules/imageResize', SafeImageResize);

const RichTextEditor = ({ name, value, onChange, placeholder, disabled, section, szakId: propSzakId }) => {
  const quillRef = useRef(null);
  const { szakId: paramSzakId } = useParams();
  const szakId = propSzakId || paramSzakId;
  const [,setIsDragging] = useState(false);

  // Helper function to compress large images
  const compressImage = useCallback((file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max 1920x1080 for performance)
        let { width, height } = img;
        const maxWidth = 1920;
        const maxHeight = 1080;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', 0.8); // 80% quality
      };
      
      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Upload image to server
  const uploadImageToServer = useCallback(async (file) => {
    console.log('Uploading image to server...', { file: file.name, szakId, section });
    
    if (!szakId) {
      console.error('No szakId available for image upload');
      alert('Image upload failed: Missing szakId parameter.');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      if (section) {
        formData.append('section', section);
      }

      console.log('Sending upload request to:', `/upload/richtext/${szakId}`);
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload/richtext/${szakId}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Image uploaded successfully:', result);
      console.log('Full server response:', JSON.stringify(result, null, 2));
      
      // Check if the URL is properly formatted
      if (result.url) {
        console.log('Returned URL:', result.url);
        console.log('URL type:', typeof result.url);
        console.log('URL starts with http:', result.url.startsWith('http'));
      }
      
      return result.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      return null;
    }
  }, [szakId, section]);

  // Convert file to server upload and insert into editor
  const insertImageFromFile = useCallback(async (file) => {
    if (!quillRef.current) {
      console.error('Quill ref not available');
      return;
    }
    
    console.log('Processing image file:', file.name, file.size, 'type:', file.type);
    
    try {
      // Check file size and compress if needed
      const maxSize = 10 * 1024 * 1024; // 10MB
      let processedFile = file;
      
      if (file.size > maxSize) {
        console.log('File too large, compressing...');
        processedFile = await compressImage(file);
        console.log('Compressed file size:', processedFile.size);
      }
      
      // Upload to server
      console.log('About to upload to server...');
      const imageUrl = await uploadImageToServer(processedFile);
      console.log('Server upload result:', imageUrl);
      
      if (imageUrl) {
        // Insert image URL into editor
        const quill = quillRef.current.getEditor();
        if (!quill) {
          console.error('Quill editor not available');
          return;
        }
        
        const range = quill.getSelection();
        const index = range ? range.index : quill.getLength();
        
        console.log('Inserting image at index:', index, 'URL:', imageUrl);
        
        // Test if the image URL is accessible
        const testImg = new Image();
        testImg.onload = () => {
          console.log('Image URL is valid and accessible');
        };
        testImg.onerror = () => {
          console.error('Image URL is not accessible:', imageUrl);
        };
        testImg.src = imageUrl;
        
        // Insert image at cursor position
        quill.insertEmbed(index, 'image', imageUrl);
        
        // Move cursor after the image
        quill.setSelection(index + 1);
        
        // Force a re-render to ensure the image loads
        setTimeout(() => {
          const imgs = quill.root.querySelectorAll('img');
          const lastImg = imgs[imgs.length - 1];
          if (lastImg && lastImg.src === imageUrl) {
            console.log('Image element found in DOM:', lastImg.src);
            if (!lastImg.complete) {
              console.log('Image is still loading...');
              lastImg.onload = () => console.log('Image loaded successfully');
              lastImg.onerror = () => console.error('Image failed to load in DOM');
            } else {
              console.log('Image already loaded');
            }
          }
        }, 100);
        
        console.log('Image inserted successfully at index:', index);
      } else {
        console.error('No image URL returned from server');
      }
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try again.');
    }
  }, [uploadImageToServer, compressImage]);

  // Handle image paste from clipboard
  const handlePaste = useCallback((e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    const items = clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if item is an image
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault(); // Prevent default paste behavior
        
        if (!szakId) {
          console.error('Cannot paste image: No szakId available');
          alert('Image paste is not available: Missing szakId parameter.');
          return;
        }
        
        const file = item.getAsFile();
        if (file) {
          insertImageFromFile(file);
        }
      }
    }
  }, [insertImageFromFile, szakId]);

  // Handle drag and drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (!szakId) {
      console.error('Cannot drop images: No szakId available');
      alert('Image drop is not available: Missing szakId parameter.');
      return;
    }
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    imageFiles.forEach(file => {
      insertImageFromFile(file);
    });

    // Remove drag styling
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.classList.remove('ql-paste-image');
    }
  }, [insertImageFromFile, szakId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
    
    // Add drag styling
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.classList.add('ql-paste-image');
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    // Remove drag styling
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.classList.remove('ql-paste-image');
    }
  }, []);

  // Handle image upload via toolbar button
  const imageHandler = useCallback(() => {
    console.log('Image button clicked, szakId:', szakId);
    
    if (!szakId) {
      console.error('Cannot upload image: No szakId available');
      alert('Image upload is not available: Missing szakId parameter.');
      return;
    }
    
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    
    input.onchange = (event) => {
      const file = event.target.files[0];
      console.log('File selected via button:', file?.name, file?.size);
      if (file) {
        insertImageFromFile(file);
      }
    };
    
    input.click();
  }, [insertImageFromFile, szakId]);

  // Handle image deletion events safely
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const selection = quill.getSelection();
        if (!selection) return;

        const [blot] = quill.getLeaf(selection.index);
        if (blot && blot.domNode && blot.domNode.tagName === 'IMG') {
          // Handle image deletion safely
          try {
            const range = quill.getSelection();
            if (range) {
              quill.deleteText(range.index, 1);
              e.preventDefault();
              e.stopPropagation();
            }
          } catch (error) {
            console.warn('Image deletion handled safely:', error);
          }
        }
      }
    };

    const quill = quillRef.current?.getEditor();
    if (quill && quill.root) {
      quill.root.addEventListener('keydown', handleKeyDown);
      
      return () => {
        quill.root.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);



  return (
    <div className="rich-text-editor-container">
      <ReactQuill
        ref={quillRef}
        name={name}
        value={value}
        onChange={onChange}
        onPaste={handlePaste} 
        onDrop={handleDrop} 
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        placeholder={placeholder}
        readOnly={disabled} 
        modules={{
          toolbar: {
            container: [
              ['bold', 'italic', 'underline'],
              [{ color: [] }],
              [{ align: [] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['image'],
            ],
            handlers: {
              image: imageHandler,
            },
          },
          imageResize: {
            modules: ['Resize', 'DisplaySize'],
            preserveAspectRatio: true,
            handleStyles: {
              backgroundColor: '#4285f4',
              border: '1px solid #ffffff',
              borderRadius: '4px',
              width: '8px',
              height: '8px',
            },
            displayStyles: {
              backgroundColor: 'rgba(0,0,0,0.8)',
              border: 'none',
              color: 'white',
              fontSize: '12px',
              fontFamily: 'Arial, sans-serif',
              padding: '4px 8px',
              borderRadius: '4px',
            },
          },
        }}
        formats={[
          'bold', 'italic', 'underline', 'color', 'background', 'align',
          'list', 'bullet', 'image'
        ]}
      />
    </div>
  );
};

export default RichTextEditor;
